import $ from "jquery";
import {Component, h} from "preact";
import {isBeforeDate} from "mojave/date/compare";
import {ICON_CHECKBOX, ICON_CHECKBOX_CHECKED} from "../../lib/icons";
import {formatDate} from "mojave/date/format";
import {formatTechnicalDate, generateStartMonths, getSelectionDateLabel, isNextMonth} from "../../lib/date";


/**
 * Mapping of all validators.
 * Validators are functions that take a value and return either an error message (in case of failure) or
 * `null` (in case of success).
 *
 * @type {{
 *     notBlank: function(string): ?string,
 *     birthday: function(string): ?string,
 *     areaCode: function(string): ?string,
 * }}
 */
const VALIDATORS = {
	notBlank (value)
	{
		return null === value
			? "Bitte einen Wert eingeben."
			: null;
	},
	birthday (value)
	{
		if (null === value)
		{
			return null;
		}

		const match = /^(\d{1,2})\.(\d{1,2}).(\d{4})$/.exec(value);

		if (null === match)
		{
			return "Bitte den Geburtstag im Format TT.MM.JJJJ eingeben.";
		}

		const day = parseInt(match[1], 10);
		const month = parseInt(match[2], 10);

		if (0 === day || 0 === month)
		{
			return "Bitte ein gültiges Datum eingeben.";
		}


		const date = new Date(match[3], month - 1, day);

		// check whether the date is in the future
		const today = new Date();
		const today16YearsAgo = new Date(today.getFullYear() - 16, today.getMonth(), today.getDate());

		return isBeforeDate(today16YearsAgo, date)
			? "Sie müssen mindestens 16 Jahre alt sein, um einen Vertrag abschließen zu können."
			: null;
	},
	areaCode (value)
	{
		if (null === value)
		{
			return null;
		}

		return /^[0-9]{4,5}$/.test(value)
			? null
			: "Bitte eine gültige Postleitzahl eingeben.";
	},
	email (value)
	{
		if (null === value)
		{
			return null;
		}

		return /^.+@\S+\.\S+$/.test(value)
			? null
			: "Bitte eine gültige E-Mail-Adresse eingeben.";
	},
	iban (value)
	{
		if (null === value)
		{
			return null;
		}

		return /^(DE\d{2}\d{8}\d{10}|AT\d{2}\d{5}\d{11}|CH\d{2}\d{5}[\dA-Z]{12})$/.test(value)
			? null
			: "Bitte eine gültige IBAN aus DE / AT / CH eingeben.";
	},
};


/**
 * @typedef {{
 *      onProceed: function(Membership.FormData),
 *      firstDay: string|null,
 * }} UserDataFormStepContent.Props
 *
 * @typedef {{
 * }} UserDataFormStepContent.State
 */
export class UserDataFormStepContent extends Component
{
	/**
	 * The list of field constraints.
	 *
	 * This list needs to be kept in-sync with the server side constraint list in the PHP class {@see DataValidator}
	 *
	 * @private
	 * @constant
	 * @type {Object<string, function()[]>}
	 */
	FIELD_CONSTRAINTS = {
		contractStart: [VALIDATORS.notBlank],
		salutation: [VALIDATORS.notBlank],
		title: [],
		lastName: [VALIDATORS.notBlank],
		firstName: [VALIDATORS.notBlank],
		birthday: [VALIDATORS.notBlank, VALIDATORS.birthday],
		street: [VALIDATORS.notBlank],
		areaCode: [VALIDATORS.notBlank, VALIDATORS.areaCode],
		city: [VALIDATORS.notBlank],
		phone: [],
		mobile: [VALIDATORS.notBlank],
		email: [VALIDATORS.notBlank, VALIDATORS.email],

		// optional fields
		presenteeActive: [],
		presenteeName: [VALIDATORS.notBlank],
		presenteeBirthday: [VALIDATORS.notBlank, VALIDATORS.birthday],
		presenteeEmail: [VALIDATORS.notBlank, VALIDATORS.email],
		presenteeStreet: [VALIDATORS.notBlank],
		presenteeAreaCode: [VALIDATORS.notBlank, VALIDATORS.areaCode],
		presenteeCity: [VALIDATORS.notBlank],

		// rest of the regular fields
		iban: [VALIDATORS.notBlank, VALIDATORS.iban],
		bic: [VALIDATORS.notBlank],
		accountHolder: [VALIDATORS.notBlank],
		accountInstitute: [VALIDATORS.notBlank],
	};

	/**
	 * @inheritDoc
	 */
	shouldComponentUpdate (nextProps, nextState, nextContext)
	{
		// never update, as otherwise the inputs might be reset
		return false;
	}


	/**
	 * @param {UserDataFormStepContent.Props} props
	 * @param {UserDataFormStepContent.State} state
	 * @returns {null|JSX.Element}
	 */
	render (props, state)
	{
		const possibleStartDates = generateStartMonths(props.firstDay);

		return (
			<div class="_membership-form">
				<div class="_membership-headline">Mache Deine Angaben</div>
				<div class="_membership-form-fields">
					<div className="_membership-form-field _membership-is-required" data-validators="not_blank">
						<label className="_membership-form-label">
							<span>Vertragsstart</span>
							<select name="contractStart" required>
								{possibleStartDates.map((date) => (
									<option value={formatTechnicalDate(date)}>
										{getSelectionDateLabel(date, props.firstDay)}
									</option>
								))}
							</select>
						</label>
					</div>
					<div class="_membership-form-field _membership-is-required" data-validators="not_blank">
						<label class="_membership-form-label">
							<span>Anrede</span>
							<select name="salutation" required={true}>
								<option value="" selected={true} disabled={true}>- bitte wählen -</option>
								<option value="f">Frau</option>
								<option value="m">Herr</option>
							</select>
						</label>
					</div>
					<div class="_membership-form-field">
						<label class="_membership-form-label">
							<span>Titel</span>
							<select name="title">
								<option value="" selected={true} disabled={true}>(kein Titel)</option>
								<option value="dr">Dr.</option>
								<option value="prof">Prof.</option>
								<option value="prof_dr">Prof. Dr.</option>
							</select>
						</label>
					</div>

					<div class="_membership-form-field _membership-is-required">
						<label class="_membership-form-label">
							<span>Nachname</span>
							<input type="text" name="lastName" required={true} value=""/>
						</label>
					</div>
					<div class="_membership-form-field _membership-is-required">
						<label class="_membership-form-label">
							<span>Vorname</span>
							<input type="text" name="firstName" required={true} value=""/>
						</label>
					</div>
					<div class="_membership-form-field _membership-is-required">
						<label class="_membership-form-label">
							<span>Geburtstag</span>
							<input type="text" name="birthday" placeholder="TT.MM.JJJJ" value="" required={true}/>
						</label>
					</div>

					<div class="_membership-form-field _membership-is-required">
						<label class="_membership-form-label">
							<span>Straße / Hausnummer</span>
							<input type="text" name="street" value="" required={true}/>
						</label>
					</div>
					<div class="_membership-form-field _membership-is-required">
						<label class="_membership-form-label">
							<span>Postleitzahl</span>
							<input type="text" name="areaCode" value="" required={true}/>
						</label>
					</div>
					<div class="_membership-form-field _membership-is-required">
						<label class="_membership-form-label">
							<span>Ort</span>
							<input type="text" name="city" value="" required={true}/>
						</label>
					</div>

					<div class="_membership-form-field">
						<label class="_membership-form-label">
							<span>Telefon</span>
							<input type="text" name="phone" value=""/>
						</label>
					</div>
					<div class="_membership-form-field _membership-is-required">
						<label class="_membership-form-label">
							<span>Mobilnummer</span>
							<input type="text" name="mobile" value="" required={true}/>
						</label>
					</div>
					<div class="_membership-form-field _membership-is-required">
						<label class="_membership-form-label">
							<span>E-Mail</span>
							<input type="email" name="email" value="" required={true}/>
						</label>
					</div>
				</div>

				<div class="_membership-form-fields _membership-optional-section">
					<input type="checkbox" class="_membership-form-optional-check" name="presenteeActive" value="1"/>
					<button type="button" class="_membership-form-optional-button">
						<span class="_membership-button-icon-inactive">{ICON_CHECKBOX}</span>
						<span class="_membership-button-icon-active">{ICON_CHECKBOX_CHECKED}</span>
						<span class="_membership-button-text">Vertragsnutzer abweichend?</span>
					</button>
					<div class="_membership-optional-section-content">
						<div class="_membership-form-optional _membership-form-fields">
							<div class="_membership-form-field _membership-is-required">
								<label class="_membership-form-label">
									<span>Kompletter Name</span>
									<input type="text" name="presenteeName" value="" required={true}/>
								</label>
							</div>
							<div className="_membership-form-field _membership-is-required">
								<label className="_membership-form-label">
									<span>Geburtstag</span>
									<input
										type="text" name="presenteeBirthday" placeholder="TT.MM.JJJJ" value=""
										required={true}
									/>
								</label>
							</div>
							<div class="_membership-form-field _membership-is-required">
								<label class="_membership-form-label">
									<span>E-Mail</span>
									<input type="email" name="presenteeEmail" value="" required={true}/>
								</label>
							</div>
							<div class="_membership-form-field _membership-is-required">
								<label class="_membership-form-label">
									<span>Straße / Hausnummer</span>
									<input type="text" name="presenteeStreet" value="" required={true}/>
								</label>
							</div>
							<div class="_membership-form-field _membership-is-required">
								<label class="_membership-form-label">
									<span>Postleitzahl</span>
									<input type="text" name="presenteeAreaCode" value="" required={true}/>
								</label>
							</div>
							<div class="_membership-form-field _membership-is-required">
								<label class="_membership-form-label">
									<span>Stadt</span>
									<input type="text" name="presenteeCity" value="" required={true}/>
								</label>
							</div>
						</div>
					</div>
				</div>

				<div class="_membership-form-fields">
					<div class="_membership-form-field _membership-is-required">
						<label class="_membership-form-label">
							<span>IBAN</span>
							<input type="text" name="iban" value="" required={true}/>
						</label>
					</div>
					<div class="_membership-form-field _membership-is-required">
						<label class="_membership-form-label">
							<span>BIC</span>
							<input type="text" name="bic" value="" required={true}/>
						</label>
					</div>
					<div class="_membership-form-field _membership-is-required">
						<label class="_membership-form-label">
							<span>Kontoinhaber</span>
							<input type="text" name="accountHolder" value="" required={true}/>
						</label>
					</div>
					<div class="_membership-form-field _membership-is-required">
						<label class="_membership-form-label">
							<span>Bank</span>
							<input type="text" name="accountInstitute" value="" required={true}/>
						</label>
					</div>
				</div>

				<div class="_membership-proceed">
					<button
						type="button" class="_membership-proceed-button"
						onClick={() => this.onProceedClick()}
					>Weiter
					</button>
				</div>
			</div>
		);
	}


	/**
	 * @inheritDoc
	 */
	componentDidMount ()
	{
		// Register event listener for toggling the hidden input
		$(this.base).on("click", "._membership-form-optional-button",
			/** @param {Event} event */
			(event) =>
			{
				const $button = $(event.currentTarget);
				const $content = $button.next();
				const $input = $button.prev();

				const isActive = !$input.prop("checked");
				$input.prop("checked", isActive);
				const method = isActive ? "slideDown" : "slideUp";
				$content.stop(true)[method]();
			}
		);
	}


	/**
	 * Handles the click on the proceed button
	 *
	 * @private
	 */
	onProceedClick ()
	{
		this.removeValidationErrors();
		const formData = this.validateForm();

		if (null !== formData && typeof this.props.onProceed === "function")
		{
			this.props.onProceed(formData);
		}
		else
		{
			// scroll to the top of the form to display the error messages
			$(window).animate({
				scrollTop: $(this.base).offset().top,
			});
		}
	}


	/**
	 * Removes all validation errors
	 *
	 * @private
	 */
	removeValidationErrors ()
	{
		const $base = $(this.base);

		$base.find("._membership-form-error")
			.remove();

		$base.find("._membership-has-error")
			.removeClass("_membership-has-error");
	}


	/**
	 * Validates the given form
	 *
	 * @private
	 * @returns {?Membership.FormData}   either the form data or `null` if there is a validation error
	 */
	validateForm ()
	{
		let hasError = false;

		// the optional fields checkbox is not inside a form-field, so it won't be catched in the list down there
		const $optionalFieldsInput = $(this.base).find("._membership-form-optional-check");
		let optionalFieldsRequired = $optionalFieldsInput.prop("checked");
		const formData = {
			[$optionalFieldsInput.attr("name")]: optionalFieldsRequired ? $optionalFieldsInput.attr("value") : null,
		};

		$(this.base).find("._membership-form-field")
			.each(
				(index, element) =>
				{
					let $field = $(element);
					let $input = $field.find("input, select");
					let name = $input.attr("name");
					let value = $input.is("select")
						? $input.find(":selected").val()
						: $input.val().trim();

					// normalize an empty input to null
					if ("" === value)
					{
						value = null;
					}

					let constraints = this.FIELD_CONSTRAINTS[name] || [];

					// the presentee fields are only active, if the presentee Checkbox is set
					if (/^presentee/.test(name) && !optionalFieldsRequired)
					{
						constraints = [];
					}

					formData[name] = value;

					constraints.forEach(
						constraint =>
						{
							let errorMessage = constraint(value);

							if (null !== errorMessage)
							{
								hasError = true;
								this.addError($field, errorMessage);
							}
						}
					);
				}
			);

		return !hasError
			? formData
			: null;
	}


	/**
	 * Adds an error to a field
	 *
	 * @private
	 * @param {JQuery} $field
	 * @param {string} message
	 */
	addError ($field, message)
	{
		$field
			.addClass("_membership-has-error")
			.append(
				$("<div></div>", {
					class: "_membership-form-error",
					text: `Fehler: ${message}`,
				})
			);
	}
}
